/** Customer Statements (design §7) — contact list + running-balance ledger.
 *  Contacts and ledger rows are derived live from documents + payments.
 *  The PDF action renders a branded statement of account and hands it to the
 *  browser's print pipeline (Save as PDF) — the data is client-derived, so
 *  there's no server PDF endpoint to call. */
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useBaseCurrency } from '../context/currencyContext';
import { useResponsive, MAX_CONTENT_WIDTH } from '../utils/responsive';
import { useSession } from '../context/sessionContext';
import { fmtMoney, initials, money } from '../utils/format';
import SkIcon from '../components/skIcon';
import { showToast } from '../components/skToast';
import { listDocuments } from '../services/billing/document.service';
import { listPayments } from '../services/billing/payment.service';
import { balanceLabel, buildStatements } from '../utils/statementLedger';

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});

interface Row { date: string; ref: string; kind: string; debit: number; credit: number; run: number; dot: string; }
interface Contact { id: string; name: string; meta: string; invoiced: number; received: number; credited: number; sub: string; rows: Row[]; }

const ts = (d: string): number => {
  const t = new Date(d).getTime();
  return isNaN(t) ? 0 : t;
};

const fmtDate = (s: string): string => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/* Gradient avatars — same hashing family used across the app. */
const AV: Array<[string, string]> = [
  ['#6366f1', '#8b5cf6'], ['#0ea5e9', '#6366f1'], ['#10b981', '#059669'],
  ['#f59e0b', '#ea580c'], ['#ec4899', '#8b5cf6'], ['#06b6d4', '#0891b2'],
];
const avatarOf = (name: string): [string, string] => {
  let h = 0;
  for (let i = 0; i < (name || '?').length; i++) h = (h + name.charCodeAt(i)) % AV.length;
  return AV[h];
};

/* ── PDF: branded statement of account → browser print/save ─────────── */
const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m] as string));

const statementHtml = (orgName: string, c: Contact, bal: number, cur: string): string => {
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const dates = c.rows.map((r) => ts(r.date)).filter(Boolean);
  const period = dates.length
    ? `${fmtDate(new Date(Math.min(...dates)).toISOString())} – ${fmtDate(new Date(Math.max(...dates)).toISOString())}`
    : '—';
  const rows = c.rows
    .map(
      (r) => `<tr>
        <td><span class="ref">${esc(r.ref)}</span><span class="sub">${esc(fmtDate(r.date))} · ${esc(r.kind)}</span></td>
        <td class="num">${r.debit ? esc(fmtMoney(r.debit, cur)) : '—'}</td>
        <td class="num credit">${r.credit ? esc(fmtMoney(r.credit, cur)) : '—'}</td>
        <td class="num bold">${esc(fmtMoney(r.run, cur))}</td>
      </tr>`,
    )
    .join('');
  return `<!doctype html><html><head><meta charset="utf-8"/>
<title>Statement — ${esc(c.name)}</title>
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #0f172a; background: #f1f5f9; }
  .page { max-width: 760px; margin: 24px auto; background: #fff; border-radius: 14px; overflow: hidden; box-shadow: 0 10px 40px rgba(15,23,42,.12); }
  .head { padding: 32px 36px; background: linear-gradient(135deg, #1e293b, #4f46e5); color: #fff; display: flex; justify-content: space-between; align-items: flex-start; }
  .brand { font-size: 20px; font-weight: 700; letter-spacing: -.3px; }
  .brand small { display:block; font-size: 11px; font-weight: 500; opacity: .8; letter-spacing: 2px; text-transform: uppercase; margin-top: 4px; }
  .doc-title { text-align: right; }
  .doc-title b { font-size: 13px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; opacity: .9; }
  .doc-title span { display:block; font-size: 12px; opacity: .75; margin-top: 6px; }
  .meta { display: flex; justify-content: space-between; padding: 24px 36px 8px; }
  .lbl { font-size: 10px; font-weight: 700; letter-spacing: 1.4px; text-transform: uppercase; color: #94a3b8; }
  .val { font-size: 15px; font-weight: 600; color: #0f172a; margin: 3px 0 14px; }
  .meta .right { text-align: right; }
  .cards { display: flex; gap: 12px; padding: 8px 36px 24px; }
  .card { flex: 1; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; background: #f8fafc; }
  .card .lbl { color: #64748b; }
  .card .amt { font-size: 18px; font-weight: 700; margin-top: 6px; font-variant-numeric: tabular-nums; }
  .card.due { background: ${bal > 0 ? '#fef2f2' : '#f0fdf4'}; border-color: ${bal > 0 ? '#fecaca' : '#bbf7d0'}; }
  .card.due .amt { color: ${bal > 0 ? '#dc2626' : '#16a34a'}; }
  table { width: 100%; border-collapse: collapse; }
  thead th { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #94a3b8; text-align: right; padding: 10px 36px; background: #f8fafc; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; }
  thead th:first-child { text-align: left; }
  tbody td { padding: 13px 36px; border-bottom: 1px solid #f1f5f9; font-size: 13px; text-align: right; font-variant-numeric: tabular-nums; }
  tbody td:first-child { text-align: left; }
  .ref { display:block; font-weight: 600; color: #0f172a; font-family: 'SF Mono', ui-monospace, Menlo, monospace; }
  .sub { display:block; font-size: 11px; color: #94a3b8; margin-top: 2px; }
  .num { color: #334155; }
  .credit { color: #16a34a; }
  .bold { font-weight: 700; color: #0f172a; }
  .totals { display: flex; justify-content: flex-end; padding: 20px 36px 32px; }
  .totals .box { min-width: 240px; }
  .totals .due-row { display:flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-radius: 10px; background: ${bal > 0 ? '#fef2f2' : '#f0fdf4'}; border: 1px solid ${bal > 0 ? '#fecaca' : '#bbf7d0'}; }
  .totals .due-row span { font-size: 12px; font-weight: 700; letter-spacing: .5px; text-transform: uppercase; color: ${bal > 0 ? '#b91c1c' : '#15803d'}; }
  .totals .due-row b { font-size: 20px; color: ${bal > 0 ? '#dc2626' : '#16a34a'}; font-variant-numeric: tabular-nums; }
  .foot { padding: 18px 36px 28px; border-top: 1px solid #f1f5f9; font-size: 11px; color: #94a3b8; text-align: center; }
  @media print { body { background: #fff; } .page { box-shadow: none; margin: 0; border-radius: 0; max-width: none; } @page { margin: 12mm; } }
</style></head>
<body>
  <div class="page">
    <div class="head">
      <div class="brand">${esc(orgName)}<small>Statement of Account</small></div>
      <div class="doc-title"><b>Statement</b><span>${esc(today)}</span></div>
    </div>
    <div class="meta">
      <div><div class="lbl">Billed to</div><div class="val">${esc(c.name)}</div></div>
      <div class="right"><div class="lbl">Statement period</div><div class="val">${esc(period)}</div></div>
    </div>
    <div class="cards">
      <div class="card"><div class="lbl">Invoiced</div><div class="amt">${esc(fmtMoney(c.invoiced, cur))}</div></div>
      <div class="card"><div class="lbl">Received</div><div class="amt">${esc(fmtMoney(c.received, cur))}</div></div>
      ${c.credited > 0 ? `<div class="card"><div class="lbl">Credit notes</div><div class="amt credit">−${esc(fmtMoney(c.credited, cur))}</div></div>` : ''}
      <div class="card due"><div class="lbl">${balanceLabel(bal).label}</div><div class="amt">${esc(fmtMoney(balanceLabel(bal).amount, cur))}</div></div>
    </div>
    <table>
      <thead><tr><th>Activity</th><th>Debit</th><th>Credit</th><th>Balance</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="4" style="text-align:center;color:#94a3b8;padding:32px">No activity in this period</td></tr>'}</tbody>
    </table>
    <div class="totals"><div class="box"><div class="due-row"><span>${balanceLabel(bal).label}</span><b>${esc(fmtMoney(balanceLabel(bal).amount, cur))}</b></div></div></div>
    <div class="foot">Generated by ${esc(orgName)} on ${esc(today)} · This statement reflects activity recorded in SK Billing.</div>
  </div>
</body></html>`;
};

const downloadStatement = (orgName: string, c: Contact, bal: number, cur: string) => {
  if (!isWeb || typeof window === 'undefined') {
    showToast('info', 'Available on web', 'Open the dashboard in a browser to export PDFs.');
    return;
  }
  const w = window.open('', '_blank', 'width=900,height=1100');
  if (!w) {
    showToast('warn', 'Pop-up blocked', 'Allow pop-ups for this site to download the statement.');
    return;
  }
  w.document.write(statementHtml(orgName, c, bal, cur));
  w.document.close();
  w.focus();
  // Let the new window paint before invoking the print/save dialog.
  setTimeout(() => { try { w.print(); } catch { /* user can print manually */ } }, 350);
  showToast('success', 'Statement ready', `${c.name}'s statement opened — choose “Save as PDF”.`);
};

/* ── Contact list item (hover + selected) ──────────────────────────── */
const ContactItem: React.FC<{ d: Contact; active: boolean; onPress: () => void }> = ({ d, active, onPress }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  const b = d.invoiced - d.received - d.credited;
  const overdue = d.sub === 'overdue';
  const [g1, g2] = avatarOf(d.name);
  return (
    <Pressable
      onPress={onPress}
      style={{
        flexDirection: 'row', alignItems: 'center', gap: 11, padding: 10, borderRadius: 10, marginBottom: 3,
        backgroundColor: active ? colors.accentSoft : hover ? colors.paper2 : 'transparent',
        borderWidth: 1, borderColor: active ? colors.accentLine : colors.transparent,
        ...web({ transition: 'background-color .12s ease, border-color .12s ease', cursor: 'pointer' }),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <View style={{ width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: g2, ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 2px 6px -2px ${g2}77` }) }}>
        <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff', fontFamily: font.sansBold }}>{initials(d.name)}</Text>
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: active ? colors.accentInk : colors.ink }}>{d.name}</Text>
        <Text style={{ fontFamily: font.sans, fontSize: 11, color: overdue ? colors.danger : colors.ink4 }}>{d.sub}</Text>
      </View>
      <Text style={{ fontFamily: font.monoMedium, fontSize: 12, color: b > 0 ? (overdue ? colors.danger : colors.ink2) : colors.ink4 }}>{b > 0 ? fmtMoney(b, cur) : 'settled'}</Text>
    </Pressable>
  );
};

/* ── Header action button ──────────────────────────────────────────── */
const ActionBtn: React.FC<{ icon: string; label: string; primary?: boolean; busy?: boolean; onPress: () => void }> = ({ icon, label, primary, busy, onPress }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      disabled={busy}
      style={{
        height: 34, paddingHorizontal: 13, borderRadius: 9, flexDirection: 'row', alignItems: 'center', gap: 7,
        borderWidth: 1, borderColor: primary ? colors.transparent : hover ? colors.lineStrong : colors.line,
        backgroundColor: primary ? colors.accent : hover ? colors.paper2 : colors.card,
        ...(primary
          ? web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: hover ? `0 8px 18px -5px ${colors.accent}aa` : `0 4px 10px -4px ${colors.accent}77`, transform: hover ? 'translateY(-1px)' : 'translateY(0)', transition: 'all .14s ease', cursor: 'pointer' })
          : web({ transition: 'all .14s ease', cursor: 'pointer' })),
      }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <SkIcon name={icon} size={14} color={primary ? '#fff' : colors.ink2} strokeWidth={2} />
      <Text style={{ fontFamily: font.sansMedium, fontSize: 12.5, fontWeight: '500', color: primary ? '#fff' : colors.ink2 }}>{label}</Text>
    </Pressable>
  );
};

/* ── Activity ledger row (hover) ───────────────────────────────────── */
const LedgerRow: React.FC<{ r: Row; last: boolean }> = ({ r, last }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  return (
    <View
      style={{ flexDirection: 'row', paddingVertical: 11, paddingHorizontal: 12, borderBottomWidth: last ? 0 : 1, borderColor: colors.line, alignItems: 'center', backgroundColor: hover ? colors.paper2 : colors.transparent, ...web({ transition: 'background-color .1s ease' }) }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <View style={{ flex: 1.4, flexDirection: 'row', alignItems: 'center', gap: 9 }}>
        <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: r.dot, ...web({ boxShadow: `0 0 0 3px ${r.dot}22` }) }} />
        <View style={{ minWidth: 0 }}>
          <Text style={{ fontFamily: font.monoSemibold, fontSize: 12.5, fontWeight: '600', color: colors.ink }}>{r.ref}</Text>
          <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4, marginTop: 1 }}>{fmtDate(r.date)} · {r.kind}</Text>
        </View>
      </View>
      <Text style={{ width: 100, textAlign: 'right', fontFamily: font.mono, fontSize: 12.5, color: r.debit ? colors.ink2 : colors.ink4 }}>{r.debit ? fmtMoney(r.debit, cur) : '—'}</Text>
      <Text style={{ width: 100, textAlign: 'right', fontFamily: font.mono, fontSize: 12.5, color: r.credit ? colors.success : colors.ink4 }}>{r.credit ? fmtMoney(r.credit, cur) : '—'}</Text>
      <Text style={{ width: 110, textAlign: 'right', fontFamily: font.monoSemibold, fontSize: 12.5, fontWeight: '600', color: colors.ink }}>{fmtMoney(r.run, cur)}</Text>
    </View>
  );
};

const Statements: React.FC = () => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const { isWide } = useResponsive();
  const { org } = useSession();
  const orgName = org?.name || 'SK Billing';
  const [docs, setDocs] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [credits, setCredits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([listDocuments({ type: 'INVOICE' }), listPayments(), listDocuments({ type: 'CREDIT_NOTE' })])
      .then(([d, p, cn]) => {
        if (!alive) return;
        if (d.status === 'fulfilled' && Array.isArray(d.value)) setDocs(d.value);
        if (p.status === 'fulfilled' && Array.isArray(p.value)) setPayments(p.value.filter((x: any) => x.direction === 'RECEIVED'));
        if (cn.status === 'fulfilled' && Array.isArray(cn.value)) setCredits(cn.value);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const DATA: Contact[] = useMemo(
    () => buildStatements(docs, credits, payments, { info: colors.info, danger: colors.danger, accent: colors.accent, success: colors.success }),
    [docs, payments, credits, colors],
  );

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, ...theme.shadow('sm') } as const;

  if (loading || DATA.length === 0) {
    return (
      <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
        <View style={[card, { padding: 56, alignItems: 'center' }]}>
          {loading ? (
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3 }}>Loading…</Text>
          ) : (
            <>
              <View style={{ width: 58, height: 58, borderRadius: 17, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, alignItems: 'center', justifyContent: 'center', marginBottom: 16, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
                <SkIcon name="fileText" size={24} color={colors.accentInk} strokeWidth={2} />
              </View>
              <Text style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: 16, color: colors.ink, letterSpacing: -0.2 }}>No statements yet</Text>
              <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3, marginTop: 5 }}>Issue an invoice to build a customer statement.</Text>
            </>
          )}
        </View>
      </View>
    );
  }

  const selId = sel && DATA.some((d) => d.id === sel) ? sel : DATA[0].id;
  const c = DATA.find((d) => d.id === selId) || DATA[0];
  const bal = c.invoiced - c.received - c.credited;
  const [g1, g2] = avatarOf(c.name);
  const statusTone = bal <= 0 ? { soft: colors.successSoft, ink: colors.success, line: colors.successLine, label: 'Settled' }
    : c.sub === 'overdue' ? { soft: colors.dangerSoft, ink: colors.danger, line: colors.dangerLine, label: 'Overdue' }
      : { soft: colors.warnSoft, ink: colors.warn, line: colors.warnLine, label: c.sub };

  const kpis: Array<{ l: string; v: string; icon: string; tone?: 'due' }> = [
    { l: 'Invoiced', v: fmtMoney(c.invoiced, cur), icon: 'fileText' },
    { l: 'Received', v: fmtMoney(c.received, cur), icon: 'checkCircle' },
    ...(c.credited > 0 ? [{ l: 'Credited', v: `−${fmtMoney(c.credited, cur)}`, icon: 'creditNote' }] : []),
    { l: balanceLabel(bal).label, v: fmtMoney(balanceLabel(bal).amount, cur), icon: 'wallet', tone: 'due' as const },
  ];

  return (
    <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16, flexDirection: isWide ? 'row' : 'column', gap: 16 }}>
      {/* Contacts rail */}
      <View style={[card, { width: isWide ? 264 : undefined, overflow: 'hidden' }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 13, paddingHorizontal: 15, borderBottomWidth: 1, borderColor: colors.line }}>
          <SkIcon name="building" size={15} color={colors.ink4} strokeWidth={2} />
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 11, fontWeight: '600', color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.6 }}>Contacts</Text>
          <View style={{ flex: 1 }} />
          <View style={{ paddingHorizontal: 8, height: 20, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontFamily: font.monoMedium, fontSize: 10.5, color: colors.ink3 }}>{DATA.length}</Text>
          </View>
        </View>
        <ScrollView style={{ maxHeight: isWide ? 560 : undefined }} contentContainerStyle={{ padding: 9 }}>
          {DATA.map((d) => (
            <ContactItem key={d.id} d={d} active={d.id === selId} onPress={() => setSel(d.id)} />
          ))}
        </ScrollView>
      </View>

      {/* Statement panel */}
      <View style={[card, { flex: 1, overflow: 'hidden' }]}>
        {/* Header */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 13, padding: 18, borderBottomWidth: 1, borderColor: colors.line, flexWrap: 'wrap' }}>
          <View style={{ width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center', backgroundColor: g2, ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 4px 12px -3px ${g2}88` }) }}>
            <Text style={{ fontFamily: font.sansBold, fontSize: 14, fontWeight: '700', color: '#fff' }}>{initials(c.name)}</Text>
          </View>
          <View style={{ minWidth: 0 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
              <Text numberOfLines={1} style={{ fontFamily: font.sansBold, fontSize: 17, fontWeight: '700', color: colors.ink, letterSpacing: -0.3 }}>{c.name}</Text>
              <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: statusTone.soft, borderWidth: 1, borderColor: statusTone.line }}>
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 9.5, fontWeight: '600', letterSpacing: 0.3, color: statusTone.ink, textTransform: 'capitalize' }}>{statusTone.label}</Text>
              </View>
            </View>
            <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4, marginTop: 2 }}>{c.rows.length} {c.rows.length === 1 ? 'entry' : 'entries'} · statement of account</Text>
          </View>
          <View style={{ flex: 1 }} />
          <View style={{ flexDirection: 'row', gap: 9 }}>
            <ActionBtn icon="send" label="Email statement" onPress={() => showToast('info', 'Email statement', 'Connect an email channel to send statements automatically.')} />
            <ActionBtn icon="download" label="Download PDF" primary onPress={() => downloadStatement(orgName, c, bal, cur)} />
          </View>
        </View>

        {/* Body */}
        <View style={{ padding: 18 }}>
          {/* KPI tiles */}
          <View style={{ flexDirection: 'row', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            {kpis.map((k) => {
              const due = k.tone === 'due';
              const tileBg = due ? (bal > 0 ? colors.dangerSoft : colors.successSoft) : colors.paper2;
              const tileLine = due ? (bal > 0 ? colors.dangerLine : colors.successLine) : colors.line;
              const valColor = due ? (bal > 0 ? colors.danger : colors.success) : colors.ink;
              const iconColor = due ? (bal > 0 ? colors.danger : colors.success) : colors.ink3;
              return (
                <View key={k.l} style={{ flex: 1, minWidth: 150, padding: 14, backgroundColor: tileBg, borderRadius: 11, borderWidth: 1, borderColor: tileLine }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                    <SkIcon name={k.icon} size={13} color={iconColor} strokeWidth={2} />
                    <Text style={{ fontFamily: font.sansMedium, fontSize: 11, color: due ? valColor : colors.ink3, fontWeight: '500' }}>{k.l}</Text>
                  </View>
                  <Text style={{ fontFamily: font.monoSemibold, fontSize: 19, fontWeight: '700', color: valColor, marginTop: 7 }}>{k.v}</Text>
                </View>
              );
            })}
          </View>

          {/* Ledger */}
          <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 11, overflow: 'hidden' }}>
            <View style={{ flexDirection: 'row', backgroundColor: colors.paper2, borderBottomWidth: 1, borderColor: colors.line, paddingVertical: 9, paddingHorizontal: 12, ...web({ backgroundImage: `linear-gradient(180deg, ${colors.paper2}, ${colors.paper})` }) }}>
              <Text style={{ flex: 1.4, fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Activity</Text>
              <Text style={{ width: 100, textAlign: 'right', fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Debit</Text>
              <Text style={{ width: 100, textAlign: 'right', fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Credit</Text>
              <Text style={{ width: 110, textAlign: 'right', fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Balance</Text>
            </View>
            {c.rows.map((r, i) => (
              <LedgerRow key={i} r={r} last={i === c.rows.length - 1} />
            ))}
            {/* Closing balance */}
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 12, borderTopWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2 }}>
              <Text style={{ flex: 1, fontFamily: font.sansSemibold, fontSize: 11.5, fontWeight: '600', color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.5 }}>Closing balance</Text>
              <Text style={{ fontFamily: font.monoSemibold, fontSize: 15, fontWeight: '700', color: bal > 0 ? colors.danger : colors.success }}>{fmtMoney(bal, cur)}</Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export default Statements;
